import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Pill } from './Pill.js';

// Turns the card into one option of a radiogroup: only the checked card is tabbable,
// and the caller moves selection and focus on arrow keys.
interface SelectableCardRadio {
  checked: boolean;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
}

// Marks the card as the trigger of a menu it opens.
interface SelectableCardMenu {
  id: string;
  expanded: boolean;
}

interface SelectableCardProps {
  testId: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  accent?: string;
  ariaPressed?: boolean;
  radio?: SelectableCardRadio;
  menu?: SelectableCardMenu;
  icon: ReactNode;
  title: string;
  tooltip?: string | null;
  badge?: ReactNode;
  subtitle?: string | null;
  note?: string | null;
  trailing?: ReactNode;
  description?: string | null;
  detail?: string | null;
  chips?: string[];
  footer?: string | null;
}

export function SelectableCard({
  testId,
  onClick,
  accent,
  ariaPressed,
  radio,
  menu,
  icon,
  title,
  tooltip,
  badge,
  subtitle,
  note,
  trailing,
  description,
  detail,
  chips,
  footer,
}: SelectableCardProps): ReactNode {
  return (
    <ButtonBase
      data-testid={testId}
      onClick={onClick}
      aria-pressed={ariaPressed}
      {...(radio === undefined
        ? {}
        : {
            role: 'radio',
            'aria-checked': radio.checked,
            tabIndex: radio.checked ? 0 : -1,
            onKeyDown: radio.onKeyDown,
          })}
      {...(menu === undefined
        ? {}
        : {
            'aria-haspopup': 'menu' as const,
            'aria-expanded': menu.expanded,
            ...(menu.expanded ? { 'aria-controls': menu.id } : {}),
          })}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        textAlign: 'left',
        gap: 3,
        p: 4,
        height: '100%',
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: accent ?? 'divider',
        '&:hover': { borderColor: accent ?? 'primary.main' },
        '&.Mui-focusVisible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      <Stack direction="row" sx={{ gap: 2, alignItems: 'flex-start', minWidth: 0 }}>
        {icon}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{title}</Typography>
            {tooltip ? (
              <Tooltip title={tooltip}>
                <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
              </Tooltip>
            ) : null}
            {badge}
          </Stack>
          {subtitle === null || subtitle === undefined ? null : (
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
              {subtitle}
            </Typography>
          )}
          {note === null || note === undefined ? null : (
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>
              {note}
            </Typography>
          )}
        </Box>
        {trailing === undefined ? null : <Box sx={{ flexShrink: 0 }}>{trailing}</Box>}
      </Stack>

      {(description === null || description === undefined) &&
      (detail === null || detail === undefined) ? null : (
        <Stack sx={{ gap: 1 }}>
          {description === null || description === undefined ? null : (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{description}</Typography>
          )}
          {detail === null || detail === undefined ? null : (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{detail}</Typography>
          )}
        </Stack>
      )}

      {chips === undefined || chips.length === 0 ? null : (
        <Stack direction="row" sx={{ gap: 1.5, flexWrap: 'wrap' }}>
          {chips.map((chip) => (
            <Pill key={chip}>{chip}</Pill>
          ))}
        </Stack>
      )}

      {footer === null || footer === undefined ? null : (
        <Typography sx={{ mt: 'auto', fontSize: 11, color: 'text.disabled' }}>{footer}</Typography>
      )}
    </ButtonBase>
  );
}
